import { useEffect, useRef, useState } from 'react'

const WEEKDAYS = ['日', '一', '二', '三', '四', '五', '六']

// 按北京时间取"今天"，避免服务器与浏览器时区不一致导致差一天
function todayStr() {
  return new Date().toLocaleDateString('sv-SE', { timeZone: 'Asia/Shanghai' }) // sv-SE 即 YYYY-MM-DD
}
const ymd = (y, m, d) =>
  `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`

export default function DateRangePicker({ from, to, onChange, single = false, id, 'aria-label': ariaLabel }) {
  const [open, setOpen] = useState(false)
  const [cursor, setCursor] = useState(() => {
    const base = from || todayStr()
    const [y, m] = base.split('-').map(Number)
    return { y, m: m - 1 }
  })
  // 选择过程中的临时起点：点第一下存起点，点第二下才提交区间
  const [picking, setPicking] = useState(null)
  const [hover, setHover] = useState(null)
  const boxRef = useRef(null)
  const triggerRef = useRef(null)

  useEffect(() => {
    if (!open) return
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  // 上次点了一天就关掉了（没点完第二下），picking 会一直留着没清。下次打开如果还带着
  // 这个陈旧的起点，第一下点击就会被当成「第二下」，拿旧起点配新终点拼出一个莫名其妙的区间。
  // 每次打开都重新开始，不用先点"清除"才能选新的
  useEffect(() => { if (open) { setPicking(null); setHover(null) } }, [open])

  const { y, m } = cursor
  const first = new Date(y, m, 1)
  const startPad = first.getDay()
  const daysInMonth = new Date(y, m + 1, 0).getDate()
  const daysInPrev = new Date(y, m, 0).getDate()
  const today = todayStr()

  // 6 行 × 7 列，补齐上下月
  const cells = []
  for (let i = 0; i < 42; i++) {
    const n = i - startPad + 1
    if (n < 1) cells.push({ day: daysInPrev + n, out: true, date: ymd(m === 0 ? y - 1 : y, m === 0 ? 11 : m - 1, daysInPrev + n) })
    else if (n > daysInMonth) cells.push({ day: n - daysInMonth, out: true, date: ymd(m === 11 ? y + 1 : y, m === 11 ? 0 : m + 1, n - daysInMonth) })
    else cells.push({ day: n, out: false, date: ymd(y, m, n) })
  }

  // 预览区间：选中起点后跟随鼠标
  const rangeStart = picking || from
  const rangeEnd = picking ? (hover || picking) : to
  const lo = rangeStart && rangeEnd ? (rangeStart <= rangeEnd ? rangeStart : rangeEnd) : null
  const hi = rangeStart && rangeEnd ? (rangeStart <= rangeEnd ? rangeEnd : rangeStart) : null

  function pick(date) {
    if (single) {
      setOpen(false)
      onChange(date, date)
      triggerRef.current?.focus()
      return
    }
    if (!picking) { setPicking(date); return }
    const a = picking <= date ? picking : date
    const b = picking <= date ? date : picking
    setPicking(null); setHover(null); setOpen(false)
    onChange(a, b)
  }

  const move = (dy, dm) => {
    let ny = y + dy, nm = m + dm
    if (nm < 0) { nm = 11; ny-- }
    if (nm > 11) { nm = 0; ny++ }
    setCursor({ y: ny, m: nm })
  }

  const label = from && to ? (from === to ? from : `${from} 至 ${to}`) : '选择日期'
  const navBtn = { background: 'none', border: 'none', color: 'var(--muted)', fontSize: 15, padding: '2px 6px', cursor: 'pointer' }

  return (
    <div className={single ? 'single-date-picker' : undefined}
      style={single ? undefined : { position: 'relative', display: 'inline-block' }} ref={boxRef}
      onKeyDown={e => {
        if (e.key === 'Escape' && open) {
          e.stopPropagation()
          setOpen(false)
          triggerRef.current?.focus()
        }
      }}>
      <button ref={triggerRef} id={id} type="button" className="btn btn-sm"
        aria-label={ariaLabel} aria-expanded={open} aria-haspopup="dialog"
        onClick={() => {
          if (!open && single) {
            const [y, m] = (from || todayStr()).split('-').map(Number)
            setCursor({ y, m: m - 1 })
          }
          setOpen(o => !o)
        }}
        style={from && to ? { borderColor: 'var(--brand)', color: 'var(--brand)' } : {}}>
        {single ? <>{label} <span aria-hidden="true">▾</span></> : <>📅 {label}</>}
      </button>

      {open && (
        <div role="dialog" aria-label={ariaLabel || '选择日期'} className="card date-picker-panel" style={{
          position: 'absolute', top: '100%', left: 0, marginTop: 6, zIndex: 50,
          padding: 16, width: 300, boxShadow: '0 10px 30px rgba(0,0,0,.12)', background: 'var(--card)',
          ...(single ? { left: 12, top: 'calc(100% - 12px)', width: 'calc(100% - 24px)', maxWidth: 300,
            maxHeight: '60vh', overflowY: 'auto', padding: 10 } : {}),
        }}>
          <div className="spread" style={{ marginBottom: 12 }}>
            <div>
              <button style={navBtn} onClick={() => move(-1, 0)} title="上一年">«</button>
              <button style={navBtn} onClick={() => move(0, -1)} title="上一月">‹</button>
            </div>
            <div style={{ fontSize: 15, fontWeight: 500 }}>{y} 年 {m + 1} 月</div>
            <div>
              <button style={navBtn} onClick={() => move(0, 1)} title="下一月">›</button>
              <button style={navBtn} onClick={() => move(1, 0)} title="下一年">»</button>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', textAlign: 'center' }}>
            {WEEKDAYS.map(w => (
              <div key={w} className="muted small" style={{ padding: '6px 0' }}>{w}</div>
            ))}
          </div>
          <div style={{ borderTop: '1px solid var(--line)', margin: '0 0 6px' }} />

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', textAlign: 'center' }}>
            {cells.map((c, i) => {
              const inRange = lo && hi && c.date >= lo && c.date <= hi
              const isEdge = c.date === lo || c.date === hi
              const isToday = c.date === today
              return (
                <button key={i} type="button" aria-label={c.date}
                  aria-pressed={single ? c.date === from : undefined}
                  onClick={() => pick(c.date)}
                  onMouseEnter={() => picking && setHover(c.date)}
                  style={{
                    border: 'none', minWidth: 0, fontFamily: 'inherit',
                    padding: '7px 0', cursor: 'pointer', fontSize: 13,
                    borderRadius: isEdge ? 7 : 0,
                    background: isEdge ? 'var(--brand)' : inRange ? 'var(--select)' : 'transparent',
                    color: isEdge ? '#fff'
                      : c.out ? 'var(--muted)'
                      : isToday ? 'var(--brand)' : 'var(--text)',
                    fontWeight: isToday || isEdge ? 600 : 400,
                  }}>
                  {c.day}
                </button>
              )
            })}
          </div>

          <div className="spread" style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--line)' }}>
            {!single && <span className="small muted">
              {picking ? '再点一天作为结束' : '点两天选择区间'}
            </span>}
            <button className="btn-link small" onClick={() => {
              setPicking(null); setHover(null); setOpen(false); onChange('', '')
              triggerRef.current?.focus()
            }}>清除</button>
          </div>
        </div>
      )}
    </div>
  )
}
